import axios from "axios";

// 創建一個新的獨立 Axios 實例
const englishCentralAxios = axios.create({
  timeout: 60000, // 設置超時時間
});

export const getEnglishCentralToken = async (
  user_id,
  name,
  email,
  native_lang = "en",
  site_lang = "en"
) => {
  // 構建查詢參數
  const queryParams = new URLSearchParams();
  queryParams.append("user_id", user_id);
  queryParams.append("name", name);
  queryParams.append("email", email);
  queryParams.append("native_lang", native_lang);
  queryParams.append("site_lang", site_lang);

  // 從環境變量獲取後端URL
  const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";

  // 構建完整URL
  const url = `${baseUrl}/api/get-token?${queryParams.toString()}`;

  try {
    // 使用獨立的 Axios 實例發送請求
    const response = await englishCentralAxios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching English Central token:", error);
    throw error;
  }
};

// 這個實例不會受到全局攔截器的影響

// 修改 getEnglishCentralRolePlays API 函數接受 token 參數
export const getEnglishCentralRolePlays = async (
  token, // 新增 token 參數
  difficultyLevels = "1,2,3",
  start = 0,
  limit = 10
) => {
  const url = `https://content.englishcentral.com/rest/roleplays?difficultyLevels=${difficultyLevels}&start=${start}&limit=${limit}`;

  const config = {
    method: "get",
    maxBodyLength: Infinity,
    url: url,
    headers: {
      Accept:
        "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
      Authorization: `JWT ${token}`, // 使用傳入的 token
    },
  };

  try {
    // 使用獨立的 Axios 實例發送請求
    const response = await englishCentralAxios.request(config);
    return response.data;
  } catch (error) {
    console.error("Error fetching English Central role plays:", error);
    throw error;
  }
};

// 修改 getEnglishCentralDialogs API 函數接受 token 參數
export const getEnglishCentralDialogs = async (
  token, // 新增 token 參數
  params = {}
) => {
  // 設置默認參數
  const defaultParams = {
    fields:
      "dialogID,title,difficulty,duration,dialogURL,thumbnailURL,demoPictureURL,seriesThumbnailURL,promotionalDialog",
    page: 0,
    pageSize: 25,
    siteLanguage: "en",
    sortBy: 2,
    difficulty: "1,2",
    goalID: 4,
  };

  // 合併用戶提供的參數和默認參數
  const mergedParams = { ...defaultParams, ...params };

  // 將參數轉換為URL查詢字符串
  const queryParams = new URLSearchParams();
  for (const key in mergedParams) {
    queryParams.append(key, mergedParams[key]);
  }

  const url = `https://bridge.englishcentral.com/rest/content/dialog?${queryParams.toString()}`;

  const config = {
    method: "get",
    maxBodyLength: Infinity,
    url: url,
    headers: {
      Accept:
        "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
      Authorization: `JWT ${token}`, // 使用傳入的 token
    },
  };

  try {
    // 使用獨立的 Axios 實例發送請求
    const response = await englishCentralAxios.request(config);
    return response.data;
  } catch (error) {
    console.error("Error fetching English Central dialogs:", error);
    throw error;
  }
};

// 獲取合作夥伴用戶的課程進度報告
export const getPartnerProgress = async (
  token,
  partnerId,
  courseIds = null,
  page = 0,
  pageSize = 200,
  reportTypes = "overallXP,courseCompletion"
) => {
  // 構建查詢參數
  const queryParams = new URLSearchParams();
  if (courseIds) queryParams.append("courseIds", courseIds);
  queryParams.append("page", page.toString());
  queryParams.append("pageSize", pageSize.toString());
  queryParams.append("reportTypes", reportTypes);

  const url = `https://reportcard.englishcentral.com/rest/report/partner/${partnerId}?${queryParams.toString()}`;

  const config = {
    method: "get",
    maxBodyLength: Infinity,
    url: url,
    headers: {
      Accept:
        "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
      "Content-Type": "application/vnd.englishcentral-v1+json",
      Authorization: `JWT ${token}`,
    },
  };

  try {
    const response = await englishCentralAxios.request(config);
    return response.data;
  } catch (error) {
    console.error("Error fetching partner progress:", error);
    throw error;
  }
};

// 獲取特定用戶的進度報告
export const getUserProgressReport = async (token, partnerId, accountId) => {
  const response = await getPartnerProgress(token, partnerId);

  if (response && response.reports) {
    return response.reports.find((report) => report.accountID === accountId);
  }

  return null;
};

// 新增：獲取課程進度數據
export const getCourseProgress = async (
  reportTypes = "overallXP,courseProgress",
  page = 0,
  pageSize = 200
) => {
  try {
    // 使用管理員帳戶獲取 token
    const adminTokenResponse = await getEnglishCentralToken(
      "igroup20100admin@englishcentral.com",
      "iGroup Admin",
      "igroup20100admin@englishcentral.com",
      "en",
      "en"
    );

    if (!adminTokenResponse || !adminTokenResponse.success) {
      throw new Error("無法獲取管理員認證令牌");
    }

    const authToken = adminTokenResponse.token;

    // 調用課程進度 API
    const url =
      "https://reportcard.englishcentral.com/rest/report/partner/20100";
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      reportTypes: reportTypes,
    });

    const response = await englishCentralAxios.get(
      `${url}?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/vnd.englishcentral-v1+json",
          Accept:
            "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
          Authorization: `JWT ${authToken}`,
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 202) {
      // 報告正在生成中
      return {
        status: "generating",
        message: "報告正在生成中，請稍後重試",
        progress: response.data?.log || "0%",
      };
    } else {
      throw new Error(`API 調用失敗: ${response.status}`);
    }
  } catch (error) {
    console.error("課程進度 API 錯誤:", error);
    throw error;
  }
};

// 新增：獲取特定用戶的課程進度
export const getUserCourseProgress = async (
  userId,
  reportTypes = "overallXP,courseProgress"
) => {
  try {
    // 使用管理員帳戶獲取 token
    const adminTokenResponse = await getEnglishCentralToken(
      "igroup20100admin@englishcentral.com",
      "iGroup Admin",
      "igroup20100admin@englishcentral.com",
      "en",
      "en"
    );

    if (!adminTokenResponse || !adminTokenResponse.success) {
      throw new Error("無法獲取管理員認證令牌");
    }

    const authToken = adminTokenResponse.token;

    // 先獲取所有進度數據
    const allProgress = await getCourseProgress(reportTypes);

    if (allProgress.status === "generating") {
      return allProgress; // 返回生成中狀態
    }

    // 從所有數據中篩選出特定用戶的數據
    if (allProgress.reports) {
      let userReport = null;

      // 首先嘗試根據 accountID 查找（如果 userId 是數字）
      if (!isNaN(userId)) {
        userReport = allProgress.reports.find(
          (report) => report.accountID.toString() === userId.toString()
        );
      }

      // 如果沒找到，嘗試根據用戶名查找
      if (!userReport) {
        userReport = allProgress.reports.find(
          (report) => report.name.toLowerCase() === userId.toLowerCase()
        );
      }

      // 如果還是沒找到，嘗試模糊匹配用戶名
      if (!userReport) {
        userReport = allProgress.reports.find(
          (report) =>
            report.name.toLowerCase().includes(userId.toLowerCase()) ||
            userId.toLowerCase().includes(report.name.toLowerCase())
        );
      }

      if (userReport) {
        console.log(
          `找到用戶: ${userReport.name} (ID: ${userReport.accountID})`
        );
        return {
          success: true,
          user_progress: userReport,
        };
      } else {
        console.log(`未找到用戶: ${userId}`);
        console.log(
          "可用用戶:",
          allProgress.reports.map((r) => `${r.name} (${r.accountID})`)
        );
        return {
          success: false,
          message: `找不到用戶 "${userId}" 的進度數據`,
        };
      }
    }

    return {
      success: false,
      message: "無法獲取進度數據",
    };
  } catch (error) {
    console.error("獲取用戶課程進度錯誤:", error);
    throw error;
  }
};

// 新增：獲取對話進度
export const getDialogProgress = async (dialogId, token) => {
  try {
    const url = `https://reportcard.englishcentral.com/rest/report/dialog/${dialogId}/progress?completed=true&incorrect=true&useNewContentWords=true`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/vnd.englishcentral-v1+json",
        Accept:
          "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取對話進度錯誤:", error);
    throw error;
  }
};

// 新增：獲取對話活動
export const getDialogActivities = async (dialogId, token) => {
  try {
    const url = `https://reportcard.englishcentral.com/rest/report/activity/dialog?dialogIDs=${dialogId}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/vnd.englishcentral-v1+json",
        Accept:
          "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取對話活動錯誤:", error);
    throw error;
  }
};

// 新增：獲取活動摘要
export const getActivitySummary = async (token) => {
  try {
    const url =
      "https://reportcard.englishcentral.com/rest/report/activity/summary";

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/vnd.englishcentral-v1+json",
        Accept:
          "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取活動摘要錯誤:", error);
    throw error;
  }
};

// 新增：獲取我的英語視頻
export const getMyEnglishVideos = async (
  token,
  page = 0,
  pageSize = 12,
  progress = "all",
  sortBy = 1
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      progress: progress,
      sortBy: sortBy.toString(),
    });

    const url = `https://reportcard.englishcentral.com/rest/report/my-english-videos?${params.toString()}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/vnd.englishcentral-v1+json",
        Accept:
          "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取我的英語視頻錯誤:", error);
    throw error;
  }
};

// 新增：獲取學生課程進度
export const getStudentCourseProgress = async (
  token,
  accountId = 13673054,
  courseIds = null
) => {
  try {
    const params = new URLSearchParams({
      account_id: accountId.toString(),
    });

    if (courseIds) {
      params.append("course_ids", courseIds);
    }

    const url = `https://reportcard.englishcentral.com/rest/report/student/course-progress?${params.toString()}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/vnd.englishcentral-v1+json",
        Accept:
          "application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取學生課程進度錯誤:", error);
    throw error;
  }
};

// ===== 新增 API 端點 =====

// 根據用戶名搜索用戶
export const findUserByName = async (token, userName) => {
  try {
    const params = new URLSearchParams({
      user_name: userName,
    });

    // 使用本地測試端口
    const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";
    const url = `${baseUrl}/api/find-user-by-name?${params.toString()}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("搜索用戶錯誤:", error);
    throw error;
  }
};

// 使用統一API獲取用戶信息 - 支持多種識別符
export const getUserInfoUnified = async (token, identifier) => {
  try {
    // 使用本地測試端口
    const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";
    const url = `${baseUrl}/api/unified/user-info/${encodeURIComponent(
      identifier
    )}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取用戶信息錯誤:", error);
    throw error;
  }
};

// 使用統一API獲取用戶詳細進度 - 包含Dialog和課程進度
export const getUserProgressUnified = async (
  token,
  identifier,
  includeDialogs = true,
  includeCourses = true
) => {
  try {
    // 使用本地測試端口
    const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";
    const url = `${baseUrl}/api/unified/user-progress/${encodeURIComponent(
      identifier
    )}?include_dialogs=${includeDialogs}&include_courses=${includeCourses}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("獲取用戶詳細進度錯誤:", error);
    throw error;
  }
};

// 獲取用戶詳細學習進度 (使用統一API，一步完成)
export const getUserDetailedProgress = async (token, userIdentifier) => {
  try {
    console.log("使用統一API獲取用戶進度，用戶標識符:", userIdentifier);

    // 使用統一API一步獲取完整的用戶進度數據（包含Dialog和課程進度）
    const response = await getUserProgressUnified(
      token,
      userIdentifier,
      true,
      true
    );

    if (!response.success) {
      throw new Error(response.message || "無法獲取用戶進度");
    }

    console.log("統一API返回的進度數據:", response);
    return response;
  } catch (error) {
    console.error("獲取用戶詳細進度錯誤:", error);
    throw error;
  }
};

// 綜合搜索和進度查詢
export const searchAndProgress = async (token, searchParams) => {
  try {
    const params = new URLSearchParams();

    if (searchParams.userName) {
      params.append("user_name", searchParams.userName);
    }
    if (searchParams.accountId) {
      params.append("account_id", searchParams.accountId);
    }
    if (searchParams.includeProgress !== undefined) {
      params.append(
        "include_progress",
        searchParams.includeProgress.toString()
      );
    }
    if (searchParams.includeActivities !== undefined) {
      params.append(
        "include_activities",
        searchParams.includeActivities.toString()
      );
    }
    if (searchParams.includeCourses !== undefined) {
      params.append("include_courses", searchParams.includeCourses.toString());
    }

    // 使用本地測試端口
    const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";
    const url = `${baseUrl}/api/search-and-progress?${params.toString()}`;

    const response = await englishCentralAxios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `JWT ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("綜合搜索和進度查詢錯誤:", error);
    throw error;
  }
};

// ===== Dialog 進度分析工具函數 =====

/**
 * 分析Dialog進度數據
 * @param {Array} dialogProgress - Dialog進度數組
 * @returns {Object} 分析結果
 */
export const analyzeDialogProgress = (dialogProgress) => {
  if (!dialogProgress || !Array.isArray(dialogProgress)) {
    return {
      totalDialogs: 0,
      completedDialogs: 0,
      inProgressDialogs: 0,
      notStartedDialogs: 0,
      completionRate: 0,
      totalPoints: 0,
      earnedPoints: 0,
      pointsPercentage: 0,
    };
  }

  let totalDialogs = dialogProgress.length;
  let completedDialogs = 0;
  let inProgressDialogs = 0;
  let notStartedDialogs = 0;
  let totalPoints = 0;
  let earnedPoints = 0;

  dialogProgress.forEach((dialog) => {
    totalPoints += dialog.totalPoints || 0;
    earnedPoints += dialog.earnedPoints || 0;

    const completionRate = dialog.progressPercentage || 0;

    if (completionRate === 100) {
      completedDialogs++;
    } else if (completionRate > 0) {
      inProgressDialogs++;
    } else {
      notStartedDialogs++;
    }
  });

  return {
    totalDialogs,
    completedDialogs,
    inProgressDialogs,
    notStartedDialogs,
    completionRate:
      totalDialogs > 0
        ? ((completedDialogs / totalDialogs) * 100).toFixed(1)
        : 0,
    totalPoints,
    earnedPoints,
    pointsPercentage:
      totalPoints > 0 ? ((earnedPoints / totalPoints) * 100).toFixed(1) : 0,
  };
};

/**
 * 獲取特定Dialog的詳細進度
 * @param {Array} dialogProgress - Dialog進度數組
 * @param {number} dialogId - Dialog ID
 * @returns {Object|null} 指定Dialog的進度數據
 */
export const getDialogById = (dialogProgress, dialogId) => {
  if (!dialogProgress || !Array.isArray(dialogProgress)) {
    return null;
  }

  return dialogProgress.find((dialog) => dialog.dialogID === dialogId) || null;
};

/**
 * 根據Dialog ID查找對應的標題
 * @param {number} dialogId - Dialog ID
 * @param {Array} dialogsList - 可用的dialogs列表（包含標題信息）
 * @returns {string} Dialog標題，如果未找到則返回默認格式
 */
export const getDialogTitleById = (dialogId, dialogsList = []) => {
  if (!dialogId) {
    return "未知對話";
  }

  if (dialogsList && Array.isArray(dialogsList)) {
    const dialog = dialogsList.find((d) => d.dialogID === dialogId);
    if (dialog && dialog.title) {
      return dialog.title;
    }
  }

  return `Dialog ${dialogId}`;
};

/**
 * 批量獲取Dialog信息（包含標題等詳細信息）
 * @param {string} token - JWT token
 * @param {Array<number>} dialogIds - Dialog ID列表
 * @returns {Promise<Array>} Dialog詳細信息列表
 */
export const getMultipleDialogInfo = async (token, dialogIds) => {
  try {
    if (!token || !dialogIds || dialogIds.length === 0) {
      return [];
    }

    console.log(`🔍 批量獲取 ${dialogIds.length} 個 Dialog 的詳細信息...`);

    // 使用 English Central 的 dialog content API 來獲取詳細信息
    // 由於單次調用可能有限制，這裡使用一個較大的 pageSize 來嘗試獲取所有相關信息
    try {
      const response = await englishCentralAxios.get(
        "https://bridge.englishcentral.com/rest/content/dialog",
        {
          params: {
            fields:
              "dialogID,title,difficulty,duration,dialogURL,thumbnailURL,demoPictureURL,seriesThumbnailURL,promotionalDialog",
            page: 0,
            pageSize: 1000, // 較大的頁面大小
            siteLanguage: "en",
            sortBy: 2,
            difficulty: "1,2,3", // 所有難度
            goalID: 4,
          },
          headers: {
            Authorization: `JWT ${token}`,
            Accept:
              "application/vnd.englishcentral-v2+json,application/json;q=0.9,*/*;q=0.8",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const allDialogs = response.data || [];
      console.log(`📋 從 API 獲取到 ${allDialogs.length} 個 Dialog 的詳細信息`);

      // 篩選出我們需要的 Dialog
      const requestedDialogs = allDialogs.filter((dialog) =>
        dialogIds.includes(dialog.dialogID)
      );

      console.log(
        `✅ 找到 ${requestedDialogs.length}/${dialogIds.length} 個請求的 Dialog`
      );

      // 對於沒找到的 Dialog，返回預設結構
      const foundIds = requestedDialogs.map((d) => d.dialogID);
      const missingIds = dialogIds.filter((id) => !foundIds.includes(id));

      const missingDialogs = missingIds.map((dialogId) => ({
        dialogID: dialogId,
        title: `Dialog ${dialogId}`,
        difficulty: 2,
        duration: "00:00:30",
      }));

      return [...requestedDialogs, ...missingDialogs];
    } catch (apiError) {
      console.warn("使用 API 獲取 Dialog 信息失敗，使用預設結構:", apiError);

      // 如果 API 調用失敗，返回預設結構
      return dialogIds.map((dialogId) => ({
        dialogID: dialogId,
        title: `Dialog ${dialogId}`,
        difficulty: 2,
        duration: "00:00:30",
      }));
    }
  } catch (error) {
    console.error("批量獲取 Dialog 信息失敗:", error);
    return [];
  }
};

/**
 * 獲取用戶最近的Dialog活動
 * @param {Array} dialogProgress - Dialog進度數組
 * @param {number} limit - 返回的最大數量
 * @returns {Array} 最近的Dialog活動列表
 */
export const getRecentDialogActivity = (dialogProgress, limit = 5) => {
  if (!dialogProgress || !Array.isArray(dialogProgress)) {
    return [];
  }

  return dialogProgress
    .filter((dialog) => dialog.lastActivity)
    .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
    .slice(0, limit)
    .map((dialog) => ({
      dialogID: dialog.dialogID,
      dialogTitle: dialog.dialogTitle,
      lastActivity: dialog.lastActivity,
      progressPercentage: dialog.progressPercentage,
      earnedPoints: dialog.earnedPoints,
    }));
};

/**
 * 錯誤處理工具函數
 * @param {Error} error - 錯誤對象
 * @param {string} context - 操作上下文
 * @returns {string} 用戶友好的錯誤消息
 */
export const handleApiError = (error, context = "操作") => {
  console.error(`${context}失敗:`, error);

  // 根據錯誤類型返回用戶友好的消息
  if (error.message.includes("User not found")) {
    return "找不到指定的用戶，請檢查用戶名或ID是否正確";
  }

  if (error.message.includes("Authentication failed")) {
    return "認證失敗，請稍後重試";
  }

  if (error.message.includes("Failed to fetch")) {
    return "網絡連接問題，請檢查網絡連接";
  }

  return error.message || `${context}失敗，請稍後重試`;
};

// ===== EnglishCentral Dialog 進度 API (優化版) =====

/**
 * 獲取用戶有進度的對話ID列表 (官方 Bridge API)
 * @param {string} token - JWT token
 * @param {string} progress - All | InProgress | GoLiveReady (默認: All)
 * @param {number} page - 頁碼 (默認: 0)
 * @param {number} pageSize - 每頁大小 (默認: 200)
 * @param {number} sortBy - 排序方式 (默認: 6 - LastViewed)
 * @returns {Promise<number[]>} 有進度的對話ID列表
 */
export const getDialogsWithProgress = async (
  token,
  progress = "All",
  page = 0,
  pageSize = 200,
  sortBy = 6
) => {
  try {
    if (!token) {
      throw new Error("請先設置認證 token");
    }

    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      progress: progress,
      sortBy: sortBy.toString(),
    });

    console.log("📡 調用 Bridge API 獲取有進度的 Dialog ID 列表...");

    // 使用官方 bridge API 獲取有進度的 Dialog ID 列表
    const response = await englishCentralAxios.get(
      `https://bridge.englishcentral.com/rest/commerce/my/english/video?${params}`,
      {
        headers: {
          Authorization: `JWT ${token}`,
          Accept:
            "application/vnd.englishcentral-v2+json,application/json;q=0.9,*/*;q=0.8",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    const data = response.data;
    console.log("🔍 Bridge API 原始回應:", data);

    // 處理不同的響應格式，提取 Dialog ID
    let dialogIds = [];

    if (Array.isArray(data)) {
      // 如果直接是 ID 數組
      dialogIds = data.filter(
        (item) => typeof item === "number" || !isNaN(parseInt(item))
      );
    } else if (data && data.dialogs) {
      // 如果有 dialogs 屬性
      dialogIds = data.dialogs
        .map((item) =>
          typeof item === "number"
            ? item
            : item.dialogID || item.videoID || item.id
        )
        .filter((id) => id && !isNaN(parseInt(id)));
    } else if (data && data.videos) {
      // 如果有 videos 屬性
      dialogIds = data.videos
        .map((item) =>
          typeof item === "number"
            ? item
            : item.dialogID || item.videoID || item.id
        )
        .filter((id) => id && !isNaN(parseInt(id)));
    }

    // 確保所有 ID 都是數字
    dialogIds = dialogIds.map((id) => parseInt(id)).filter((id) => !isNaN(id));

    console.log(`✅ Bridge API 返回 ${dialogIds.length} 個有進度的 Dialog ID`);

    return dialogIds;
  } catch (error) {
    console.error("獲取有進度 Dialog ID 列表失敗:", error);
    return [];
  }
};

/**
 * 獲取用戶有進度的對話詳細信息（兩步驟：Bridge API + Report API）
 * @param {string} token - JWT token
 * @param {string} progress - All | InProgress | GoLiveReady (默認: All)
 * @param {number} page - 頁碼 (默認: 0)
 * @param {number} pageSize - 每頁大小 (默認: 200)
 * @param {number} sortBy - 排序方式 (默認: 6 - LastViewed)
 * @param {function} onProgress - 進度回調函數
 * @returns {Promise<Object[]>} 帶詳細進度信息的對話列表
 */
export const getDialogsWithProgressDetails = async (
  token,
  progress = "All",
  page = 0,
  pageSize = 200,
  sortBy = 6,
  onProgress = null
) => {
  try {
    if (!token) {
      throw new Error("請先設置認證 token");
    }

    console.log("🚀 開始兩步驟獲取 Dialog 詳細進度...");

    // 步驟 1: 獲取有進度的 Dialog ID 列表
    console.log("📋 步驟 1: 獲取有進度的 Dialog ID 列表...");
    const dialogIds = await getDialogsWithProgress(
      token,
      progress,
      page,
      pageSize,
      sortBy
    );

    if (dialogIds.length === 0) {
      console.log("⚠️ 沒有找到有進度的 Dialog");
      return [];
    }

    console.log(`✅ 找到 ${dialogIds.length} 個有進度的 Dialog ID`);

    // 步驟 2: 批量獲取詳細進度信息
    console.log("📊 步驟 2: 批量獲取詳細進度信息...");
    const progressResults = await getMultipleDialogDetailedProgress(
      token,
      dialogIds,
      (progress) => {
        console.log(
          `獲取詳細進度: ${progress.current}/${progress.total} - Dialog ${progress.dialogId}`
        );
        if (onProgress) {
          onProgress(progress);
        }
      }
    );

    console.log(`✅ 成功獲取 ${progressResults.length} 個 Dialog 的詳細進度`);

    return progressResults;
  } catch (error) {
    console.error("獲取 Dialog 詳細進度失敗:", error);
    return [];
  }
};

/**
 * 獲取用戶活動摘要 (官方 API)
 * @param {string} token - JWT token
 * @returns {Promise<Object>} 用戶活動摘要數據
 */
export const getUserActivitySummary = async (token) => {
  try {
    if (!token) {
      throw new Error("請先設置認證 token");
    }

    // 使用官方 reportcard API
    const response = await englishCentralAxios.get(
      "https://reportcard.englishcentral.com/rest/report/activity/summary",
      {
        headers: {
          Authorization: `JWT ${token}`,
          Accept:
            "application/vnd.englishcentral-v2+json,application/json;q=0.9,*/*;q=0.8",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    const data = response.data;

    // 返回第一個用戶的數據（通常只有一個）
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error("獲取活動摘要失敗:", error);
    return null;
  }
};

/**
 * 高效獲取用戶完整進度 (推薦方法)
 * @param {string} token - JWT token
 * @param {string} progressFilter - All | InProgress | GoLiveReady
 * @param {function} onProgress - 進度回調函數
 * @returns {Promise<Object>} 完整的用戶進度數據
 */
export const getCompleteUserProgress = async (
  token,
  progressFilter = "All",
  onProgress = null
) => {
  try {
    if (!token) {
      throw new Error("請先設置認證 token");
    }

    // 1. 獲取活動摘要
    if (onProgress) onProgress({ step: "summary", message: "獲取活動摘要..." });
    const activitySummary = await getUserActivitySummary(token);

    // 2. 獲取有進度的對話列表
    if (onProgress)
      onProgress({
        step: "dialog-list",
        message: "獲取有進度的對話列表...",
      });
    const dialogIds = await getDialogsWithProgress(token, progressFilter);

    if (dialogIds.length === 0) {
      return {
        activitySummary: activitySummary,
        dialogIds: [],
        dialogProgress: [],
        totalDialogs: 0,
        message: "用戶沒有任何對話進度",
      };
    }

    // 3. 獲取詳細對話進度（只對有進度的對話）
    if (onProgress)
      onProgress({
        step: "dialog-progress",
        message: `獲取 ${dialogIds.length} 個對話的詳細進度...`,
      });

    const dialogProgress = await getMultipleDialogDetailedProgress(
      token,
      dialogIds,
      (progress) => {
        if (onProgress) {
          onProgress({
            step: "dialog-progress",
            message: `正在處理對話 ${progress.current}/${progress.total}...`,
            current: progress.current,
            total: progress.total,
          });
        }
      }
    );

    return {
      activitySummary: activitySummary,
      dialogIds: dialogIds,
      dialogProgress: dialogProgress,
      totalDialogs: dialogIds.length,
      completedDialogs: dialogProgress.filter((d) => d.isCompleted).length,
      totalPoints: dialogProgress.reduce(
        (sum, d) => sum + (d.totalPoints || 0),
        0
      ),
      analysis: analyzeDialogProgressResults(dialogProgress),
    };
  } catch (error) {
    console.error("獲取完整用戶進度失敗:", error);
    throw error;
  }
};

/**
 * 獲取單個對話的詳細學習進度
 * @param {string} token - JWT token
 * @param {number} dialogId - 對話ID
 * @returns {Promise<Object>} 對話進度數據
 */
export const getDialogDetailedProgress = async (token, dialogId) => {
  try {
    if (!token) {
      throw new Error("請先設置認證 token");
    }

    // 使用官方 EnglishCentral API
    const response = await englishCentralAxios.get(
      `https://reportcard.englishcentral.com/rest/report/dialog/${dialogId}/progress`,
      {
        headers: {
          Authorization: `JWT ${token}`,
          Accept:
            "application/vnd.englishcentral-v2+json,application/json;q=0.9,*/*;q=0.8",
        },
      }
    );

    // 檢查是否有數據
    if (!response.data || Object.keys(response.data).length === 0) {
      // 空響應表示用戶沒有該對話的進度
      return {
        dialogID: dialogId,
        hasProgress: false,
        totalPoints: 0,
        activities: [],
        message: "用戶尚未開始此對話",
        isCompleted: false,
        completionRate: 0,
      };
    }

    const data = response.data;

    return {
      ...data,
      dialogID: dialogId,
      hasProgress: true,
      isCompleted: isDialogCompleted(data),
      completionRate: calculateDialogCompletionRate(data),
      stats: getDialogDetailedStats(data),
    };
  } catch (error) {
    console.error(`獲取對話 ${dialogId} 詳細進度失敗:`, error);

    // 如果是 404 或空響應，表示用戶沒有進度
    if (error.response?.status === 404 || error.message.includes("404")) {
      return {
        dialogID: dialogId,
        hasProgress: false,
        totalPoints: 0,
        activities: [],
        message: "用戶尚未開始此對話",
        isCompleted: false,
        completionRate: 0,
      };
    }

    return {
      dialogID: dialogId,
      hasProgress: false,
      error: error.message,
      isCompleted: false,
      completionRate: 0,
    };
  }
};

/**
 * 批量獲取多個對話的詳細進度
 * @param {string} token - JWT token
 * @param {number[]} dialogIds - 對話ID數組
 * @param {function} onProgress - 進度回調函數
 * @returns {Promise<Object[]>} 所有對話的進度數據
 */
export const getMultipleDialogDetailedProgress = async (
  token,
  dialogIds,
  onProgress = null
) => {
  const results = [];
  const total = dialogIds.length;

  for (let i = 0; i < dialogIds.length; i++) {
    const dialogId = dialogIds[i];

    try {
      const progress = await getDialogDetailedProgress(token, dialogId);
      results.push(progress);

      // 調用進度回調
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: total,
          dialogId: dialogId,
          completed: progress.hasProgress,
          result: progress,
        });
      }

      // 添加延遲避免請求過快
      if (i < dialogIds.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error(`獲取對話 ${dialogId} 失敗:`, error);
      results.push({
        dialogID: dialogId,
        hasProgress: false,
        error: error.message,
        isCompleted: false,
        completionRate: 0,
      });
    }
  }

  return results;
};

/**
 * 檢查對話是否完成
 * @param {Object} dialogData - 對話進度數據
 * @returns {boolean} 是否完成
 */
export const isDialogCompleted = (dialogData) => {
  if (!dialogData.activities || dialogData.activities.length === 0) {
    return false;
  }

  return dialogData.activities.every(
    (activity) => activity.completed && activity.activityProgress >= 1.0
  );
};

/**
 * 計算對話完成率
 * @param {Object} dialogData - 對話進度數據
 * @returns {number} 完成率 (0-100)
 */
export const calculateDialogCompletionRate = (dialogData) => {
  if (!dialogData.activities || dialogData.activities.length === 0) {
    return 0;
  }

  const totalProgress = dialogData.activities.reduce(
    (sum, activity) => sum + (activity.activityProgress || 0),
    0
  );

  return Math.round((totalProgress / dialogData.activities.length) * 100);
};

/**
 * 獲取對話的詳細統計信息
 * @param {Object} dialogData - 對話進度數據
 * @returns {Object} 統計信息
 */
export const getDialogDetailedStats = (dialogData) => {
  if (!dialogData.activities || dialogData.activities.length === 0) {
    return {
      totalActivities: 0,
      completedActivities: 0,
      totalPoints: 0,
      earnedPoints: 0,
      correctAnswers: 0,
      totalAnswers: 0,
      accuracy: 0,
    };
  }

  const activities = dialogData.activities || [];
  let correctAnswers = 0;
  let totalAnswers = 0;
  let earnedPoints = 0;

  activities.forEach((activity) => {
    if (activity.completed) {
      earnedPoints += activity.activityPoints || 0;
    }

    // 統計問題回答情況
    if (activity.watchedComprehensionQuestions) {
      activity.watchedComprehensionQuestions.forEach((question) => {
        totalAnswers++;
        if (question.correct) {
          correctAnswers++;
        }
      });
    }
  });

  return {
    totalActivities: activities.length,
    completedActivities: activities.filter((a) => a.completed).length,
    totalPoints: dialogData.totalPoints || 0,
    earnedPoints: earnedPoints,
    correctAnswers: correctAnswers,
    totalAnswers: totalAnswers,
    accuracy:
      totalAnswers > 0 ? Math.round((correctAnswers / totalAnswers) * 100) : 0,
  };
};

/**
 * 分析多個對話的進度結果
 * @param {Object[]} progressResults - 進度結果數組
 * @param {Object[]} dialogList - 對話列表（包含 title 等信息）
 * @returns {Object} 分析結果
 */
export const analyzeDialogProgressResults = (
  progressResults,
  dialogList = []
) => {
  const analysis = {
    total: progressResults.length,
    withProgress: 0,
    completed: 0,
    totalPoints: 0,
    totalEarnedPoints: 0,
    averageAccuracy: 0,
    details: [],
  };

  let totalAccuracy = 0;
  let dialogsWithAccuracy = 0;

  progressResults.forEach((result) => {
    if (result.hasProgress) {
      analysis.withProgress++;
      analysis.totalPoints += result.totalPoints || 0;

      if (result.isCompleted) {
        analysis.completed++;
      }

      if (result.stats) {
        analysis.totalEarnedPoints += result.stats.earnedPoints || 0;

        if (result.stats.accuracy > 0) {
          totalAccuracy += result.stats.accuracy;
          dialogsWithAccuracy++;
        }
      }

      const dialogInfo = dialogList.find((d) => d.dialogID === result.dialogID);

      analysis.details.push({
        dialogID: result.dialogID,
        title: dialogInfo?.title || `Dialog ${result.dialogID}`,
        completed: result.isCompleted,
        completionRate: result.completionRate,
        points: result.totalPoints,
        earnedPoints: result.stats?.earnedPoints || 0,
        accuracy: result.stats?.accuracy || 0,
        stats: result.stats,
        activities: result.activities,
      });
    }
  });

  analysis.averageAccuracy =
    dialogsWithAccuracy > 0
      ? Math.round(totalAccuracy / dialogsWithAccuracy)
      : 0;

  return analysis;
};
